import React, { useContext, useEffect, useState, useMemo, useCallback } from "react";
import { config } from "../context/config";
import { displayData, mainSearch, recodeNow, update } from "../components/indexDB/indexDBStora";
import '../style/pages/sales.css';
import Pagination from "../components/global/Pagination";
import { summaryValidation } from "../funcs/templetes/summarySales";
import { CurrentTime } from "../funcs/current";
import { useTransaction } from "../funcs/hooks/usetransaction";
import { scenes } from "../context/scenes";
import { context } from "../context/context";
import { auth } from "../context/auth";

// 🔥 مكون عرض المنتجات مع memoization
const ProductList = React.memo(({ items }) => (
    <>
        {items.map(item => (
            <div key={item.id}>{item.name} × {item.quantity}</div>
        ))}
    </>
));

ProductList.displayName = 'ProductList';

const limit = 30;

export default function Sales() {
    const { stylingNav, page } = useContext(config);
    const { user } = useContext(auth);
    const { setDataEnv } = useContext(scenes);
    const { setInvoiceNum } = useContext(context);
    
    const [allInvoicesToday, setAllInvoicesToday] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [minMax, setMinMax] = useState({ min: 0, max: 0 });
    const [showReturned, setShowReturned] = useState(true);
    const [maxPages, setMaxPages] = useState(1);
    
    const [filterDate, setFilterDate] = useState("");
    const [filterCashier, setFilterCashier] = useState("");
    
    useEffect(() => {
        loadInvoices();
    }, []);

    const loadInvoices = async () => {
        setIsLoading(true);
        try {
            const data = await displayData("invoices"); // كل الفواتير

            if (!data) return;

            const filtered = data.filter(d => d.end_day === "f");
            setAllInvoicesToday(filtered);

            const paginationNumber = Math.ceil(filtered.length / limit);
            setMaxPages(paginationNumber || 1);

        } catch (error) {
            console.error("خطأ في تحميل الفواتير:", error);
        } finally {
            setIsLoading(false);
        }
    };

    // 🔥 تطبيق الفلاتر + pagination على المعروض فقط
    const displayedInvoices = useMemo(() => {
        if (!allInvoicesToday.length) return [];

        let result = [];

        for (let i = 0; i < allInvoicesToday.length; i++) {
            const order = allInvoicesToday[i];
            const isReturned = order.returned === true;

            if (!showReturned && isReturned) continue;

            if (filterDate) {
                const filterDateTime = new Date(filterDate).setHours(0,0,0,0);
                const orderDate = new Date(order.meta.createdAt).setHours(0,0,0,0);
                if (orderDate < filterDateTime) continue;
            }

            if (filterCashier && order.meta.idEmp !== filterCashier) continue;

            result.push({ ...order, isReturned });
        }

        result.sort((a,b) => b.order_number - a.order_number);

        // pagination
        const start = (page - 1) * limit;
        const end = start + limit;
        return result.slice(start, end);

    }, [allInvoicesToday, filterDate, filterCashier, showReturned, page]);

    // 🔥 حساب min/max من المعروض فقط
    useEffect(() => {
        if (displayedInvoices.length > 0) {
            let min = Infinity;
            let max = -Infinity;

            for (let i = 0; i < displayedInvoices.length; i++) {
                const total = displayedInvoices[i].total || 0;
                if (total < min) min = total;
                if (total > max) max = total;
            }
            setMinMax({ min, max });
        } else {
            setMinMax({ min: 0, max: 0 });
        }
    }, [displayedInvoices]);

    // 🔥 summary على كل الفواتير اليوم
    const summary = useMemo(() => {
        let cash = 0, delivery = 0, visa = 0, returnedCount = 0;

        for (let i = 0; i < allInvoicesToday.length; i++) {
            const order = allInvoicesToday[i];

            if (order.returned === true) {
                returnedCount++;
                continue;
            }

            if (order.client_data?.phone_number && order?.mode === "delivery") {
                delivery += order.client_data?.payment || 0;
            }

            if (order.method === "cash") cash += order.total || 0;
            else visa += order.total || 0;
        }

        return {
            cash,
            delivery,
            visa,
            returnedCount,
            orders: allInvoicesToday.length
        };
    }, [allInvoicesToday]);

    // 🔥 قائمة الكاشير
    const cashierList = useMemo(() => {
        const cashiers = new Set();
        for (let i = 0; i < allInvoicesToday.length; i++) {
            cashiers.add(allInvoicesToday[i].meta.idEmp);
        }
        return Array.from(cashiers);
    }, [allInvoicesToday]);

    // 🔥 مولد الأجزاء
    function* chunkGenerator(array, chunkSize) {
        for (let i = 0; i < array.length; i += chunkSize) {
            yield array.slice(i, i + chunkSize);
        }
    }

    // 🔥 إنهاء الشيفت
const closeShift = useCallback(async () => {
    if (!allInvoicesToday.length) {
        alert("لا توجد أوردرات لإنهاء الشيفت!");
        return;
    }

    setIsLoading(true);

    const chunkSize = 30;
    let processed = 0;
    let failed = 0;

    try {
        for (const chunk of chunkGenerator(allInvoicesToday, chunkSize)) {

            // 🔥 1️⃣ نجمع كل الخصومات للـ chunk بالكامل
            const chunkDeductions = new Map();

            chunk.forEach(order => {
                order.data.forEach(item => {
                    const { investory, barcode, quantity } = item;

                    const key = `${investory}_${barcode}`;
                    const prev = chunkDeductions.get(key) || 0;

                    chunkDeductions.set(key, prev + Number(quantity));
                });
            });

            // 🔥 2️⃣ نتحقق من الكميات الأول (Validation Phase)
            for (const [key, totalQtyToDeduct] of chunkDeductions.entries()) {

                const [warehouseId, barcode] = key.split("_");

                const inv = await mainSearch("warehouse", warehouseId);
                
                if (!inv) {
                    throw new Error("المخزن غير موجود");
                }

                const warehouse = inv;

                const boxIndex = warehouse.box.findIndex(
                    b => b.barcode === barcode
                );

                if (boxIndex === -1) {
                    throw new Error("المنتج غير موجود داخل المخزن");
                }

                const currentQty = Number(warehouse.box[boxIndex].qty);
                const newQty = currentQty - totalQtyToDeduct;

                if (newQty < 0) {
                    throw new Error(`الكمية غير كافية للباركود ${barcode}`);
                }
            }

            // 🔥 3️⃣ بعد ما اتأكدنا إن كله تمام نبدأ الخصم فعليًا
            for (const [key, totalQtyToDeduct] of chunkDeductions.entries()) {

                const [warehouseId, barcode] = key.split("_");

                const inv = await mainSearch("warehouse", warehouseId);
                const warehouse = inv;

                const boxIndex = warehouse.box.findIndex(
                    b => b.barcode === barcode &&
                        !b.returned
                );

                const currentQty = Number(warehouse.box[boxIndex].qty);
                const newQty = currentQty - totalQtyToDeduct;

                warehouse.box[boxIndex].qty = String(newQty);

                await update("warehouse", {
                    ...warehouse,
                    async: { status: "f", reason: "update" }
                });
            }

            // 🔥 4️⃣ تحديث الفواتير بعد خصم المخزن
            for (const order of chunk) {
                await update("invoices", {
                    ...order,
                    end_day: "t",
                    async: { status: "f", reason: "update" }
                });
            }

            processed += chunk.length;

            await new Promise(resolve => setTimeout(resolve, 0));
        }

        await loadInvoices();

        if (failed === 0) {
            const decision = summaryValidation(summary, CurrentTime(true, true));

            if (!end_day_transaction.decision.status) {
                alert(end_day_transaction.decision.message);
            } else {
                await recodeNow(end_day_transaction.data, "transactions");
            }

            if (!decision.status) {
                alert("حدث خطأ ما يرجى اعادة المحاولة في وقت لاحق");
                return;
            }

            await recodeNow(decision.templete, "sales");
            setDataEnv({});
            setInvoiceNum(1);
            alert("تم إنهاء الشيفت بنجاح!");
        } else {
            alert(`تم إنهاء الشيفت مع ${failed} عملية فاشلة`);
        }

    } catch (error) {
        console.error("خطأ في إنهاء الشيفت:", error);
        alert("حدث خطأ أثناء إنهاء الشيفت");
        failed++;
    } finally {
        setIsLoading(false);
    }

}, [allInvoicesToday]);

    // 🔥 تصدير الفاتورة
    const exportShortInvoice = useCallback(() => alert("قريباً - الفاتورة المختصرة"), []);
    const exportDetailedInvoice = useCallback(() => alert("قريباً - الفاتورة التفصيلية"), []);

    // reocrd transaction
    const end_day_transaction = useTransaction("end_day",`تم تقفيل الحساب لهذا اليوم: ${summary.cash} جنيه`);

    return (
        <>
        <div className={`sales-page page ${!stylingNav ? "page-grow" : "page-shrink"}`}>
            <h2>المبيعات</h2>

            {/* Filters */}
            <div className="filters-inp flex" style={{ gap: "10px", flexWrap: "wrap", marginBottom: "15px" }}>
                <div>
                    <label>من تاريخ</label><br />
                    <input 
                        type="date" 
                        value={filterDate} 
                        onChange={e => setFilterDate(e.target.value)}
                        disabled={isLoading}
                    />
                </div>

                <div>
                    <label>الكاشير</label><br />
                    <select 
                        value={filterCashier} 
                        onChange={e => setFilterCashier(e.target.value)}
                        disabled={isLoading}
                    >
                        <option value="">الكل</option>
                        {cashierList.map(emp => (
                            <option key={emp} value={emp}>{emp}</option>
                        ))}
                    </select>
                </div>

                <div>
                    <label>عرض المرتجعات</label><br />
                    <label className="switch">
                        <input 
                            type="checkbox" 
                            checked={showReturned}
                            onChange={e => setShowReturned(e.target.checked)}
                            disabled={isLoading}
                        />
                        <span className="slider round"></span>
                    </label>
                </div>

                <div>
                    <label>أقل إجمالي</label><br />
                    <input 
                        type="number" 
                        value={minMax.min} 
                        readOnly 
                        style={{ width: "80px", backgroundColor: "#f5f5f5" }}
                    />
                </div>

                <div>
                    <label>أعلى إجمالي</label><br />
                    <input 
                        type="number" 
                        value={minMax.max} 
                        readOnly 
                        style={{ width: "80px", backgroundColor: "#f5f5f5" }}
                    />
                </div>
            </div>

            {/* Buttons */}
            <div className="right btns flex" style={{ marginBottom: "15px" }}>
                <button 
                    className="error-bg" 
                    onClick={closeShift}
                    disabled={isLoading || !allInvoicesToday.length}
                >
                    {isLoading ? "جاري التحميل..." : "إنهاء الشيفت"}
                </button>
                <button 
                    className="normal-bg" 
                    onClick={exportShortInvoice}
                    disabled={isLoading}
                >
                    الفاتوره المختصره
                </button>
                <button 
                    className="success-bg" 
                    onClick={exportDetailedInvoice}
                    disabled={isLoading}
                >
                    الفاتوره التفصيلية
                </button>
            </div>

            {/* Summary */}
            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginBottom: "15px" }}>
                <Box currency={user.data[0]?.currency.code} title="كاش" value={summary.cash} />
                <Box currency={user.data[0]?.currency.code} title="دليفري" value={summary.delivery} />
                <Box currency={user.data[0]?.currency.code} title="فيزا/محفظة" value={summary.visa} />
                <Box currency={user.data[0]?.currency.code} title="عدد الطلبات" value={summary.orders} isNumber />
                <Box currency={user.data[0]?.currency.code} title="مرتجع" value={summary.returnedCount} isNumber />            
            </div>

            {/* Table */}
            <div style={{ overflowX: "auto", maxHeight: "calc(100vh - 300px)", overflowY: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
                    <thead style={{ position: "sticky", top: 0, backgroundColor: "white", zIndex: 1 }}>
                        <tr>
                            <th style={th}></th>
                            <th style={th}>#</th>
                            <th style={th}>العميل</th>
                            <th style={th}>المنتجات</th>
                            <th style={th}>الإجمالي</th>
                            <th style={th}>الكاشير</th>
                            <th style={th}>التاريخ</th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            <tr>
                                <td colSpan="7" style={{ textAlign: "center", padding: "20px" }}>جاري التحميل...</td>
                            </tr>
                        ) : displayedInvoices.length === 0 ? (
                            <tr>
                                <td colSpan="7" style={{ textAlign: "center", padding: "20px" }}>لا يوجد بيانات</td>
                            </tr>
                        ) : (
                            displayedInvoices.map(order => (
                                <tr key={order.id} className={`${order.isReturned ? "returned-row" : ""}`}>
                                    <td style={td}>{order.async.status === 't' ? "🟢" : "🔴"}</td>
                                    <td style={td}>#{order.order_number}</td>
                                    <td style={td}>{order.client_data?.client_name ? `${order.client_data.client_name} - ${order.client_data.phone_number}` : "فرع"}</td>
                                    <td style={td}><ProductList items={order.data || []} /></td>
                                    <td style={td}>
                                        <div>
                                            {`${order.total?.toFixed(2) || 0} ${user.data[0]?.currency.code}`}
                                            <span className="text-weight-bold line">{order.client_data?.payment ? `+ ${order.client_data.payment} ${user.data[0]?.currency.code}` : ""}</span>
                                        </div>
                                        <div className="right flex flex-align-item-center">
                                            {order.delivery?.deliveryID && 
                                                <p className={`status-coll inline ${order.delivery.isCollect ? "success-bg" : "error-bg"}`}>
                                                    {order.delivery.isCollect ? "تم التحصيل" : "باقي"}
                                                </p>
                                            }
                                            <p className="status-coll success-bg">{order.method}</p>
                                            {order.isReturned && <p className="status-coll error-bg" style={{ marginRight: "5px" }}>مرتجع</p>}
                                        </div>
                                    </td>
                                    <td style={td}>{order.meta?.idEmp || "غير معروف"}</td>
                                    <td style={td}>{new Date(order.meta?.createdAt).toLocaleDateString('ar-EG')}</td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
        <Pagination max={maxPages}/>
        </>
    );
}

// 🔥 مكون Box
const Box = React.memo(({ title, currency, value, isNumber = false }) => {
    const formattedValue = useMemo(() => {
        const num = typeof value === 'number' ? value : parseFloat(value) || 0;
        if (isNumber) return num;
        return `${num.toFixed(2)} ${currency}`;
    }, [value, isNumber]);
    
    return (
        <div style={boxStyle}>
            <div>{title}</div>
            <strong>{formattedValue}</strong>
        </div>
    );
});

Box.displayName = 'Box';

const boxStyle = { 
    flex: "1 1 150px", 
    border: "1px solid #ddd", 
    padding: "10px",
    backgroundColor: "white",
    borderRadius: "4px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
};

const th = { 
    border: "1px solid #ddd", 
    padding: "8px", 
    textAlign: "left",
    backgroundColor: "#f8f9fa",
    fontWeight: "bold"
};

const td = { 
    border: "1px solid #ddd", 
    padding: "8px",
    verticalAlign: "top"
};